import { baseApi } from './baseApi'

/**
 * A policy the superadmin can assign to a LAB user. `key` is an opaque string from the
 * backend registry — the FE never imports backend enums, it just round-trips these keys.
 */
export interface AvailableAccessPolicy {
  key: string
  label: string
  entity: string
}

export interface UserAccessPolicies {
  /**
   * The user's policy assignments — all editable in the admin UI. Role-bound policies (e.g. the
   * outsource role's) are materialized as real assignments when the role is applied, so they
   * appear here like any other and can be individually removed.
   */
  policyKeys: string[]
}

export const accessPolicyApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAvailableAccessPolicies: builder.query<AvailableAccessPolicy[], void>({
      query: () => ({ url: `/lab/access-policies` }),
    }),
    getUserAccessPolicies: builder.query<UserAccessPolicies, { userId: number }>({
      query: ({ userId }) => ({ url: `/lab/access-policies/users/${userId}` }),
      providesTags: (result, error, { userId }) => [{ type: 'AccessPolicyAssignment', id: userId }],
    }),
    setUserAccessPolicies: builder.mutation<UserAccessPolicies, { userId: number; policyKeys: string[] }>({
      query: ({ userId, policyKeys }) => ({
        method: `PUT`,
        url: `/lab/access-policies/users/${userId}`,
        body: { policyKeys },
      }),
      invalidatesTags: (result, error, { userId }) => [{ type: 'AccessPolicyAssignment', id: userId }],
    }),
  }),
})

export const {
  useGetAvailableAccessPoliciesQuery,
  useGetUserAccessPoliciesQuery,
  useSetUserAccessPoliciesMutation,
} = accessPolicyApi
